<template>
  <Teleport to="body">
  <transition name="modal">
    <div v-if="isOpen" class="fixed inset-0" style="z-index: 99999;">
      <div class="fixed inset-0 bg-black/50" @click="close"></div>
      <div class="fixed inset-0 flex items-center justify-center" style="pointer-events: none;">
        <div class="bg-white rounded-lg text-left overflow-hidden shadow-xl max-w-3xl w-full mx-4 relative" style="pointer-events: auto;">
          <!-- Modal Header -->
          <div class="bg-primary-50 px-6 py-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Work Activity Details</h3>
                <p class="text-sm text-gray-600 mt-1">
                  {{ formatDateTime(workItem?.created_at) }}
                  <span v-if="workItem?.appuser_name" class="ml-2">
                    by {{ workItem.appuser_name }}
                  </span>
                </p>
              </div>
              <button
                @click="close"
                class="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-xl" />
              </button>
            </div>
          </div>

          <!-- Modal Body -->
          <div class="max-h-[70vh] overflow-y-auto">
            <!-- Loading State -->
            <div v-if="loading" class="flex items-center justify-center py-12">
              <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-3xl text-primary-600" />
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="p-6">
              <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <p class="text-red-600">{{ error }}</p>
              </div>
            </div>

            <!-- Content using DynamicRenderer -->
            <div v-else-if="displaySchema && workData">
              <DynamicRenderer
                :data="workData"
                :display-schema="displaySchema"
              />
            </div>

            <!-- Fallback Raw Data Display -->
            <div v-else-if="workItem" class="p-6">
              <div class="bg-gray-50 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-2">No display configuration available. Showing raw data:</p>
                <pre class="text-xs text-gray-700 whitespace-pre-wrap">{{ JSON.stringify(workItem.metadata, null, 2) }}</pre>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              @click="close"
              class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import DynamicRenderer from '@/components/widgetcomponents/JSON_Editors/DynamicRenderer.vue';

export default {
  name: 'WorkDetailModal',
  components: {
    FontAwesomeIcon,
    DynamicRenderer
  },
  props: {
    workItem: {
      type: Object,
      default: null
    },
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      displaySchema: null,
      workData: null
    };
  },
  watch: {
    workItem: {
      immediate: true,
      handler(newItem) {
        if (newItem) {
          this.processWorkItem(newItem);
        }
      }
    }
  },
  methods: {
    processWorkItem(item) {
      if (!item) return;

      this.loading = true;
      this.error = null;

      try {
        // Extract work type configuration
        const workType = item.work_type_details || item.work_type;

        if (!workType?.configuration) {
          // No configuration, will show raw data
          this.displaySchema = null;
          this.workData = null;
          this.loading = false;
          return;
        }

        // Check which configuration format we have
        const hasNewFormat = workType.configuration.fields && Array.isArray(workType.configuration.fields);
        const hasOldFormat = workType.configuration.properties && typeof workType.configuration.properties === 'object';

        if (hasNewFormat) {
          // form_data may be nested (child survey works) or flat on metadata
          const effectiveData = item.metadata?.form_data && typeof item.metadata.form_data === 'object' && !Array.isArray(item.metadata.form_data)
            ? { ...item.metadata, ...item.metadata.form_data }
            : item.metadata;
          const schema = this.transformNewFormatToSchema(workType, effectiveData);
          // If schema produced no renderable content, fall through to raw display
          const hasContent = schema?.elements?.some(el => el.children?.length > 0);
          if (hasContent) {
            this.displaySchema = schema;
            this.workData = effectiveData;
          } else {
            this.displaySchema = null;
            this.workData = null;
          }
        } else if (hasOldFormat) {
          this.displaySchema = this.transformOldFormatToSchema(workType, item.metadata);
          this.workData = item.metadata;
        } else {
          // Unknown format
          this.displaySchema = null;
          this.workData = null;
        }
      } catch (err) {
        console.error('Error processing work item:', err);
        this.error = 'Failed to process work item data';
      } finally {
        this.loading = false;
      }
    },

    transformNewFormatToSchema(workType, metadata) {
      const elements = [];

      // Group fields into sections for better organization
      const mainSection = {
        type: 'section',
        title: workType.display_name || workType.name,
        children: []
      };

      // Get visible fields (if tracked)
      const visibleFields = metadata.visible_fields || [];

      // Sort fields by order
      const sortedFields = [...(workType.configuration.fields || [])]
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      sortedFields.forEach(field => {
        // ...
        const value = metadata[field.id];
        if (value === undefined || value === null || value === '') {
          return;
        }

        // Create display element based on field type
        const element = this.createDisplayElement(field, value, metadata);  // ✅ Pass metadata
        if (element) {
          mainSection.children.push(element);
        }
      });

      // Add metadata info section if there's additional data
      if (metadata.timestamp || metadata.task_metadata) {
        const metaSection = {
          type: 'section',
          title: 'Additional Information',
          children: []
        };

        if (metadata.timestamp) {
          metaSection.children.push({
            type: 'text',
            label: 'Submitted',
            value: this.formatDateTime(metadata.timestamp)
          });
        }

        if (metadata.task_metadata && Object.keys(metadata.task_metadata).length > 0) {
          // Add relevant task metadata
          Object.entries(metadata.task_metadata).forEach(([key, val]) => {
            if (val && !key.startsWith('_')) { // Skip internal fields
              metaSection.children.push({
                type: 'text',
                label: this.formatFieldLabel(key),
                value: String(val)
              });
            }
          });
        }

        if (metaSection.children.length > 0) {
          elements.push(metaSection);
        }
      }

      elements.unshift(mainSection);
      return { elements };
    },

    transformOldFormatToSchema(workType, metadata) {
      const elements = [];

      const mainSection = {
        type: 'section',
        title: workType.display_name || workType.name,
        children: []
      };

      // Process properties from old format
      const properties = workType.configuration.properties || {};

      Object.entries(properties).forEach(([key, config]) => {
        const value = metadata[key];
        if (value === undefined || value === null || value === '') {
          return;
        }

        const element = {
          type: 'text',
          label: config.title || this.formatFieldLabel(key),
          value: this.formatOldFormatValue(value, config)
        };

        mainSection.children.push(element);
      });

      elements.push(mainSection);
      return { elements };
    },

    createDisplayElement(field, value,metadata) {
      let element = {
        label: field.question
      };

      switch (field.type) {
        case 'section':
          return null;
        case 'text':
        case 'date':
          element.type = 'text';
          element.value = field.type === 'date' ? this.formatDate(value) : value;
          break;
        case 'nested_list':
          // For nested lists (arrays of objects with structured fields)
          element.type = 'nested_list';
          element.items = metadata[field.id] || [];
          element.fields = field.fields; // Pass through the field definitions
          element.label = field.question;
          break;
        case 'textarea':
          // For longer text, don't use a label prefix
          element = {
            type: 'section',
            title: field.question,
            children: [{
              type: 'text',
              value: value
            }]
          };
          break;

        case 'number':
          element.type = 'metric';
          element.value = value;
          element.format = 'number';
          break;

        case 'radio':
        case 'select':
          // Show the human-readable label instead of the value
          const selectedOption = field.options?.find(opt => opt.value == value);
          element.type = 'text';
          element.value = selectedOption ? selectedOption.label : value;
          break;

        case 'multi_select':
        case 'checkbox':
          // Multiple selections
          if (Array.isArray(value) && value.length > 0) {
            const selectedLabels = value.map(val => {
              const option = field.options?.find(opt => opt.value == val);
              return option ? option.label : val;
            });
            element.type = 'list';
            element.items = selectedLabels;
            element.itemTemplate = '{item}';
          } else {
            return null; // No selections
          }
          break;

        case 'boolean':
          element.type = 'text';
          element.value = value ? 'Yes' : 'No';
          break;

        case 'scale':
          const scaleOption = field.options?.find(opt => opt.value == value);
          element.type = 'metric';
          element.value = scaleOption ? scaleOption.label : value;
          break;

        default:
          element.type = 'text';
          element.value = String(value);
      }

      return element;
    },

    formatOldFormatValue(value, config) {
      // Format value based on old configuration format
      if (config.enum && Array.isArray(config.enum)) {
        // If it has enum, try to find a label
        return value;
      }

      if (config.type === 'array' && Array.isArray(value)) {
        return value.join(', ');
      }

      if (config.type === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      return String(value);
    },

    formatFieldLabel(key) {
      // Convert snake_case or camelCase to Title Case
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    formatDateTime(timestamp) {
      if (!timestamp) return 'Unknown';
      const date = new Date(timestamp);

      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
        // No timeZone = browser's local timezone
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return 'Unknown';

      // Check if it's a date-only string (YYYY-MM-DD format)
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        // Parse as local date by adding time component
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }

      // Otherwise parse as regular timestamp
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    close() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Modal transition animations */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-active .transform,
.modal-leave-active .transform {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
